import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Icon,
  Label,
  Message,
  Modal,
  Pagination,
  Segment,
  Table,
  Tab,
} from 'semantic-ui-react';
import { API, showError, showSuccess, timestamp2string } from '../../helpers';
import { ITEMS_PER_PAGE } from '../../constants';

const RecycleStation = () => {
  const [activeTab, setActiveTab] = useState(0);
  
  // 已删除的渠道
  const [deletedChannels, setDeletedChannels] = useState([]);
  const [channelLoading, setChannelLoading] = useState(false);
  const [channelPage, setChannelPage] = useState(1);
  const [channelTotal, setChannelTotal] = useState(0);

  // 已删除的令牌
  const [deletedTokens, setDeletedTokens] = useState([]);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenPage, setTokenPage] = useState(1);
  const [tokenTotal, setTokenTotal] = useState(0);

  // 确认对话框
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null);
  const [confirmType, setConfirmType] = useState(''); // 'channel' or 'token'

  // 加载已删除的渠道
  const loadDeletedChannels = async (page = 1) => {
    setChannelLoading(true);
    try {
      // 使用专用的已删除渠道接口
      const res = await API.get(`/api/channel/deleted?p=${page - 1}`);
      if (res.data.success) {
        setDeletedChannels(res.data.data || []);
        setChannelTotal(res.data.total || 0);
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(err.message);
    }
    setChannelLoading(false);
  };

  // 加载已删除的令牌
  const loadDeletedTokens = async (page = 1) => {
    setTokenLoading(true);
    try {
      // 使用专用的已删除令牌接口
      const res = await API.get(`/api/token/deleted?p=${page - 1}`);
      if (res.data.success) {
        setDeletedTokens(res.data.data || []);
        setTokenTotal(res.data.total || 0);
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(err.message);
    }
    setTokenLoading(false);
  };

  useEffect(() => {
    loadDeletedChannels(1);
    loadDeletedTokens(1);
  }, []);

  // 恢复渠道
  const handleRestoreChannel = async (id) => {
    try {
      const res = await API.post(`/api/channel/restore/${id}`);
      if (res.data.success) {
        showSuccess('渠道已恢复');
        loadDeletedChannels(channelPage);
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  // 恢复令牌
  const handleRestoreToken = async (id) => {
    try {
      const res = await API.post(`/api/token/restore/${id}`);
      if (res.data.success) {
        showSuccess('令牌已恢复');
        loadDeletedTokens(tokenPage);
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  // 彻底删除渠道
  const handlePermanentDeleteChannel = async (id) => {
    try {
      const res = await API.delete(`/api/channel/${id}`);
      if (res.data.success) {
        showSuccess('渠道已彻底删除');
        loadDeletedChannels(channelPage);
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  // 彻底删除令牌
  const handlePermanentDeleteToken = async (id) => {
    try {
      const res = await API.delete(`/api/token/${id}`);
      if (res.data.success) {
        showSuccess('令牌已彻底删除');
        loadDeletedTokens(tokenPage);
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  // 显示确认对话框
  const showConfirm = (action, item, type) => {
    setConfirmAction(() => action);
    setConfirmItem(item);
    setConfirmType(type);
    setConfirmOpen(true);
  };

  // 确认操作
  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (confirmAction) {
      await confirmAction(confirmItem.id);
    }
  };

  // 渠道表格
  const renderChannelsTable = () => {
    const totalPages = Math.ceil(channelTotal / ITEMS_PER_PAGE);

    return (
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>ID</Table.HeaderCell>
            <Table.HeaderCell>名称</Table.HeaderCell>
            <Table.HeaderCell>类型</Table.HeaderCell>
            <Table.HeaderCell>模型</Table.HeaderCell>
            <Table.HeaderCell>删除时间</Table.HeaderCell>
            <Table.HeaderCell>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {deletedChannels.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan='6' textAlign='center'>
                暂无已删除的渠道
              </Table.Cell>
            </Table.Row>
          ) : (
            deletedChannels.map((channel) => (
              <Table.Row key={channel.id}>
                <Table.Cell>{channel.id}</Table.Cell>
                <Table.Cell>{channel.name}</Table.Cell>
                <Table.Cell>
                  <Label size='small'>{channel.type}</Label>
                </Table.Cell>
                <Table.Cell>
                  <code style={{ fontSize: '0.85em' }}>
                    {channel.models?.substring(0, 50)}
                    {channel.models?.length > 50 ? '...' : ''}
                  </code>
                </Table.Cell>
                <Table.Cell>{timestamp2string(channel.created_time)}</Table.Cell>
                <Table.Cell>
                  <Button
                    size='small'
                    positive
                    onClick={() => showConfirm(handleRestoreChannel, channel, 'channel')}
                  >
                    <Icon name='refresh' />
                    恢复
                  </Button>
                  <Button
                    size='small'
                    negative
                    onClick={() => showConfirm(handlePermanentDeleteChannel, channel, 'channel')}
                  >
                    <Icon name='trash' />
                    彻底删除
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))
          )}
        </Table.Body>
        {channelTotal > ITEMS_PER_PAGE && (
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan='6'>
                <Pagination
                  activePage={channelPage}
                  totalPages={totalPages}
                  onPageChange={(e, { activePage }) => {
                    setChannelPage(activePage);
                    loadDeletedChannels(activePage);
                  }}
                  size='small'
                  floated='right'
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        )}
      </Table>
    );
  };

  // 令牌表格
  const renderTokensTable = () => {
    const totalPages = Math.ceil(tokenTotal / ITEMS_PER_PAGE);

    return (
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>ID</Table.HeaderCell>
            <Table.HeaderCell>名称</Table.HeaderCell>
            <Table.HeaderCell>额度</Table.HeaderCell>
            <Table.HeaderCell>已用</Table.HeaderCell>
            <Table.HeaderCell>删除时间</Table.HeaderCell>
            <Table.HeaderCell>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {deletedTokens.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan='6' textAlign='center'>
                暂无已删除的令牌
              </Table.Cell>
            </Table.Row>
          ) : (
            deletedTokens.map((token) => (
              <Table.Row key={token.id}>
                <Table.Cell>{token.id}</Table.Cell>
                <Table.Cell>{token.name}</Table.Cell>
                <Table.Cell>
                  {token.unlimited_quota ? '无限' : token.remain_quota?.toFixed(2)}
                </Table.Cell>
                <Table.Cell>{token.used_quota?.toFixed(2)}</Table.Cell>
                <Table.Cell>{timestamp2string(token.created_time)}</Table.Cell>
                <Table.Cell>
                  <Button
                    size='small'
                    positive
                    onClick={() => showConfirm(handleRestoreToken, token, 'token')}
                  >
                    <Icon name='refresh' />
                    恢复
                  </Button>
                  <Button
                    size='small'
                    negative
                    onClick={() => showConfirm(handlePermanentDeleteToken, token, 'token')}
                  >
                    <Icon name='trash' />
                    彻底删除
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))
          )}
        </Table.Body>
        {tokenTotal > ITEMS_PER_PAGE && (
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan='6'>
                <Pagination
                  activePage={tokenPage}
                  totalPages={totalPages}
                  onPageChange={(e, { activePage }) => {
                    setTokenPage(activePage);
                    loadDeletedTokens(activePage);
                  }}
                  size='small'
                  floated='right'
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        )}
      </Table>
    );
  };

  // Tab 配置
  const panes = [
    {
      menuItem: {
        key: 'channels',
        icon: 'server',
        content: '已删除渠道',
      },
      render: () => (
        <Tab.Pane loading={channelLoading}>
          {renderChannelsTable()}
        </Tab.Pane>
      ),
    },
    {
      menuItem: {
        key: 'tokens',
        icon: 'key',
        content: '已删除令牌',
      },
      render: () => (
        <Tab.Pane loading={tokenLoading}>
          {renderTokensTable()}
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            <Icon name='trash outline' />
            回收站
          </Card.Header>
          <Card.Description>
            管理已删除的渠道和令牌，可以恢复或彻底删除
          </Card.Description>
        </Card.Content>
        <Card.Content>
          <Message info>
            <Message.Header>提示</Message.Header>
            <p>
              • 软删除的渠道会保留在回收站中，可以随时恢复<br/>
              • 恢复渠道时会自动重建模型关联<br/>
              • 彻底删除将无法恢复，请谨慎操作
            </p>
          </Message>

          <Tab
            panes={panes}
            activeIndex={activeTab}
            onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
          />
        </Card.Content>
      </Card>

      {/* 确认对话框 */}
      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        size='mini'
      >
        <Modal.Header>确认操作</Modal.Header>
        <Modal.Content>
          <p>
            {confirmType === 'channel' && confirmAction === handleRestoreChannel && (
              <>确定要恢复渠道 <strong>{confirmItem?.name}</strong> 吗？</>
            )}
            {confirmType === 'channel' && confirmAction === handlePermanentDeleteChannel && (
              <>确定要彻底删除渠道 <strong>{confirmItem?.name}</strong> 吗？此操作不可恢复！</>
            )}
            {confirmType === 'token' && confirmAction === handleRestoreToken && (
              <>确定要恢复令牌 <strong>{confirmItem?.name}</strong> 吗？</>
            )}
            {confirmType === 'token' && confirmAction === handlePermanentDeleteToken && (
              <>确定要彻底删除令牌 <strong>{confirmItem?.name}</strong> 吗？此操作不可恢复！</>
            )}
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setConfirmOpen(false)}>取消</Button>
          <Button
            color={confirmAction?.name?.includes('Permanent') ? 'red' : 'green'}
            onClick={handleConfirm}
          >
            <Icon name={confirmAction?.name?.includes('Permanent') ? 'trash' : 'check'} />
            {confirmAction?.name?.includes('Permanent') ? '彻底删除' : '恢复'}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default RecycleStation;
